import type { MoneyV2 } from "@shopify/hydrogen/storefront-api-types"

import * as UI from "@iyk/ui"

import { toSentenceCase } from "@iyk/string"
import { Money } from "@shopify/hydrogen"
import { useLang } from "../../lib/lang/use-lang.ts"

/**
 * Renders the product price with optional compare at price
 * @param size - The size of the price text
 * @param props - Additional props passed to container div
 * @returns The product price with optional compare at price
 */
export const ProductPrice = ({
  price,
  compareAtPrice,
  size,
  className,
  availableForSale,
  ...props
}: ProductPriceProps) => {
  return (
    <div className={classForProductPrice({ size, className })} {...props}>
      <ProductPriceContent {...{ price, compareAtPrice, availableForSale }} />
    </div>
  )
}

function showCompareAtPrice(price: MoneyV2, compareAtPrice: MoneyV2) {
  const priceAmount = Number(price.amount ?? 0)
  const compareAtPriceAmount = Number(compareAtPrice.amount ?? 0)
  return compareAtPriceAmount > priceAmount
}

function ProductPriceContent({ price, compareAtPrice, availableForSale }: ProductData) {
  const lang = useLang()

  if (availableForSale === false) {
    return <p className="text-gray-11">{toSentenceCase(lang.terms.SOLD_OUT)}</p>
  }

  return (
    <>
      {compareAtPrice && showCompareAtPrice(price, compareAtPrice) && (
        <Money data={compareAtPrice} className="text-gray-11 line-through" />
      )}
      <Money data={price} />
    </>
  )
}

const classForProductPrice = UI.cva(["flex items-center gap-2"], {
  variants: {
    size: {
      sm: "text-sm",
      md: "text-base",
    },
  },
  defaultVariants: {
    size: "md",
  },
})

type ProductData = {
  price: MoneyV2
  compareAtPrice?: MoneyV2 | null
  availableForSale?: boolean
}

type ProductPriceProps = UI.VariantProps<typeof classForProductPrice> &
  React.ComponentProps<"div"> &
  ProductData
